import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const fieldBase =
  "mbf-body w-full bg-transparent border-b-2 border-[#0A355C]/15 focus:border-[#2E7D32] outline-none py-2.5 text-[#0A355C] placeholder:text-[#0A355C]/35 transition-colors duration-300";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <section id="contact" className="py-24 bg-[#FBF9F3] scroll-mt-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
            Get in Touch
          </span>
          <h2 className="mbf-display mt-4 text-4xl font-semibold text-[#0A355C] leading-tight tracking-tight">
            Let's Build Something Together
          </h2>
          <Stitch className="w-24 h-2 mx-auto mt-5" color="#2E7D32" />
          <p className="mbf-body mt-6 text-[#514a3d] leading-8">
            Whether you want to enrol, volunteer, or partner with the Trust —
            send us a note and someone from our team will write back.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7 bg-white border border-[#0A355C]/10 p-8 md:p-10">
            {sent ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-14 h-14 rounded-full border-2 border-[#0A355C] flex items-center justify-center mb-5">
                  <Send className="w-6 h-6 text-[#0A355C]" />
                </div>
                <h3 className="mbf-display text-xl font-semibold text-[#0A355C]">
                  Message sent
                </h3>
                <p className="mbf-body text-[14px] text-[#514a3d] mt-2 max-w-xs">
                  Thank you for reaching out — we'll be in touch shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="mbf-mono text-[11px] uppercase tracking-wide text-[#0A355C]/60">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className={fieldBase}
                    />
                  </div>
                  <div>
                    <label className="mbf-mono text-[11px] uppercase tracking-wide text-[#0A355C]/60">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className={fieldBase}
                    />
                  </div>
                </div>

                <div>
                  <label className="mbf-mono text-[11px] uppercase tracking-wide text-[#0A355C]/60">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    required
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us what you're looking for"
                    className={`${fieldBase} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  className="relative inline-flex items-center gap-2 pl-6 pr-5 py-3.5 text-[#0A355C] font-semibold transition-transform duration-200 hover:-translate-y-0.5"
                  style={{
                    background: "#E0A526",
                    clipPath: "polygon(14px 0, 100% 0, 100% 100%, 14px 100%, 0 50%)",
                  }}
                >
                  <span className="absolute left-[7px] top-1/2 -translate-y-1/2 w-[6px] h-[6px] rounded-full bg-[#0A355C]/70 ring-2 ring-[#E0A526]" />
                  Send Message
                </button>
              </form>
            )}
          </div>

          {/* Info — mailing-label tag */}
          <div className="lg:col-span-5">
            <div
              className="bg-[#0A355C] text-white p-8 md:p-9 h-full"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 20px 100%, 0 88%)" }}
            >
              <span className="mbf-mono text-[11px] uppercase tracking-[0.18em] text-[#E0A526]">
                Reach Us
              </span>
              <Stitch className="w-16 h-2 mt-4" color="#E0A526" />

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full border-2 border-[#E0A526] flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-[#E0A526]" />
                  </div>
                  <p className="mbf-body text-[14px] text-blue-100/80 leading-relaxed pt-1.5">
                    No. 2/116 C, First Street,
                    <br />
                    Samy Nagar, Bagayam,
                    <br />
                    Vellore – 632002, Tamil Nadu
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full border-2 border-[#E0A526] flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4 text-[#E0A526]" />
                  </div>
                      <a href="tel:+91 7208058502" className="mbf-body text-[14px] text-blue-100/80 hover:text-white transition-colors">
                    +91 7208058502
                  </a>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full border-2 border-[#E0A526] flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-[#E0A526]" />
                  </div>
                  <a href="mailto:contact@mbfoundationsdl.org" className="mbf-body text-[14px] text-blue-100/80 hover:text-white transition-colors">
                    contact@mbfoundationsdl.org
                  </a>
                </div>
              </div>

              <p className="mbf-mono text-[10.5px] text-blue-100/40 mt-10 tracking-wide">
                REG. PUBLIC CHARITABLE TRUST — EST. 18.09.2015
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;